import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shirt, LayoutGrid } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIZES = ["S", "M", "L", "XL", "XXL"];
const POSTES = ["Gardien", "Défenseur central", "Latéral droit", "Latéral gauche", "Milieu défensif", "Milieu central", "Milieu offensif", "Ailier droit", "Ailier gauche", "Attaquant"];

const isInscrit = (r) => r.paiement_statut === "payée";

function PlayerLine({ r, gold }) {
  return (
    <li className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-[#27272A]/60 last:border-0">
      <span className={gold ? "text-white" : "text-[#A1A1AA]"}>{r.prenom} {r.nom}</span>
      <span className="text-[10px] uppercase tracking-wider text-[#71717A] shrink-0">
        {r.taille_maillot ? `Maillot ${r.taille_maillot}` : "—"}
      </span>
    </li>
  );
}

export default function AdminLogistics() {
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API}/admin/registrations`, { withCredentials: true })
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Erreur de chargement des inscriptions"));
  }, []);
  useEffect(load, [load]);

  const inscrits = items.filter(isInscrit);
  const preinscrits = items.filter((r) => !isInscrit(r));

  const sizeRows = [...SIZES, null].map((s) => ({
    size: s || "—",
    ins: inscrits.filter((r) => (r.taille_maillot || null) === s).length,
    pre: preinscrits.filter((r) => (r.taille_maillot || null) === s).length,
  })).filter((r) => r.size !== "—" || r.ins + r.pre > 0);

  const byPoste = POSTES.map((p) => ({
    poste: p,
    ins: inscrits.filter((r) => r.poste_principal === p),
    pre: preinscrits.filter((r) => r.poste_principal === p),
  })).filter((g) => g.ins.length + g.pre.length > 0);

  return (
    <div data-testid="admin-logistics" className="space-y-14">
      <section>
        <div className="flex items-center gap-3 mb-2">
          <Shirt size={18} className="text-[#C5A059]" />
          <h2 className="font-display text-xl uppercase font-bold">Maillots — tailles à prévoir</h2>
        </div>
        <p className="text-sm text-[#71717A] mb-6">
          {inscrits.length} inscrit(s) payé(s) · {preinscrits.length} pré-inscrit(s) en attente de paiement
        </p>
        <div className="overflow-x-auto border border-[#27272A] max-w-2xl">
          <table className="w-full text-sm" data-testid="jersey-totals-table">
            <thead>
              <tr className="bg-[#121214] text-left text-[10px] uppercase tracking-widest text-[#71717A]">
                <th className="px-4 py-3 font-medium">Taille</th>
                <th className="px-4 py-3 font-medium">Inscrits (payés)</th>
                <th className="px-4 py-3 font-medium">Pré-inscrits</th>
                <th className="px-4 py-3 font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {sizeRows.map((r) => (
                <tr key={r.size} data-testid={`jersey-row-${r.size}`} className="border-t border-[#27272A]">
                  <td className="px-4 py-3 font-display font-bold text-[#C5A059]">{r.size}</td>
                  <td className="px-4 py-3 text-white">{r.ins}</td>
                  <td className="px-4 py-3 text-[#A1A1AA]">{r.pre}</td>
                  <td className="px-4 py-3 font-semibold">{r.ins + r.pre}</td>
                </tr>
              ))}
              <tr className="border-t border-[#C5A059]/40 bg-[#121214]" data-testid="jersey-row-total">
                <td className="px-4 py-3 font-display font-bold uppercase text-xs tracking-widest">Total</td>
                <td className="px-4 py-3 font-semibold text-white">{inscrits.length}</td>
                <td className="px-4 py-3 font-semibold text-[#A1A1AA]">{preinscrits.length}</td>
                <td className="px-4 py-3 font-semibold">{items.length}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <div className="flex items-center gap-3 mb-6">
          <LayoutGrid size={18} className="text-[#C5A059]" />
          <h2 className="font-display text-xl uppercase font-bold">Joueurs par poste</h2>
        </div>
        {byPoste.length === 0 && (
          <p className="text-sm text-[#71717A] border border-[#27272A] p-8 text-center">Aucune inscription pour le moment.</p>
        )}
        <div className="grid md:grid-cols-2 gap-6">
          {byPoste.map((g) => (
            <div key={g.poste} data-testid={`position-group-${g.poste.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="border border-[#27272A] bg-[#121214] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display uppercase font-bold">{g.poste}</h3>
                <span className="text-[10px] uppercase tracking-widest text-[#71717A]">{g.ins.length + g.pre.length} joueur(s)</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#C5A059] mb-2">Inscrits · {g.ins.length}</p>
                  {g.ins.length === 0 ? (
                    <p className="text-xs text-[#3f3f46]">—</p>
                  ) : (
                    <ul>{g.ins.map((r) => <PlayerLine key={r.id} r={r} gold />)}</ul>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Pré-inscrits · {g.pre.length}</p>
                  {g.pre.length === 0 ? (
                    <p className="text-xs text-[#3f3f46]">—</p>
                  ) : (
                    <ul>{g.pre.map((r) => <PlayerLine key={r.id} r={r} />)}</ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
